/**
 * Google Review Prompt Modal — web port of
 * `/app/mobile/src/components/ReviewPromptModal.tsx` (Phase R7, 2026-07-01).
 *
 * Keeps the same backend contract:
 *   • Opens on `visible={true}` with the `prompt` payload returned by
 *     `GET /api/me/review-prompt/should-show`.
 *   • Fires `POST /api/me/review-prompt/track` with action='clicked'
 *     | 'dismissed' | 'never_again_this_brand' on CTA / dismiss.
 *   • Opens the Google Review URL in a new tab on CTA click.
 *   • Backdrop click + ESC key + Maybe-later CTA all map to 'dismissed'.
 *
 * Copy is overridable per-trigger via optional props so the receipt-
 * claim flow can use the operator's R7 wording while the existing
 * stamp-earned / reward-redeemed triggers (if ever ported to web) can
 * keep their backend-provided copy.
 */
import { useEffect } from 'react';
import { Star, ExternalLink, X } from 'lucide-react';

import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';

/** @typedef {{
 *   id: string;
 *   url: string;
 *   brand_id: string;
 *   branch_id: string | null;
 * }} ReviewLink
 */
/** @typedef {{
 *   show: true;
 *   review_link: ReviewLink;
 *   brand: { id: string; name: string; logo_url: string };
 *   branch_name: string;
 *   copy: { title: string; body: string };
 *   prompt_event_id: string;
 * }} ShownPrompt */

/**
 * @param {{
 *   visible: boolean,
 *   prompt: ShownPrompt | null,
 *   onClose: () => void,
 *   titleOverride?: string,
 *   bodyOverride?: string,
 *   primaryCtaOverride?: string,
 *   secondaryCtaOverride?: string,
 *   neverAgainLabel?: string | null,
 *   testIdPrefix?: string,
 * }} props
 */
export default function ReviewPromptModal({
  visible,
  prompt,
  onClose,
  titleOverride,
  bodyOverride,
  primaryCtaOverride,
  secondaryCtaOverride,
  neverAgainLabel = "Don't show again for this brand",
  testIdPrefix = 'review-prompt',
}) {
  const token = useAuthStore((s) => s.token);

  // ESC-to-dismiss
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') void handleAction('dismissed');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleAction = async (action) => {
    if (!token || !prompt) {
      onClose();
      return;
    }
    // Fire the tracking call (fire-and-forget — failure must not block
    // the modal close or external link).
    try {
      await api.reviewPromptTrack(token, {
        reviewLinkId: prompt.review_link.id,
        action,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('reviewPromptTrack failed', err);
    }
    if (action === 'clicked') {
      // External Google Review URL. Open in a new tab per the operator's
      // "external browser on mobile, new tab on web" directive.
      try {
        window.open(prompt.review_link.url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('open review link failed', err);
      }
    }
    onClose();
  };

  if (!visible || !prompt) return null;

  const title = titleOverride ?? prompt.copy.title;
  const body = bodyOverride ?? prompt.copy.body;
  const primaryLabel = primaryCtaOverride ?? 'Leave a Google review';
  const secondaryLabel = secondaryCtaOverride ?? 'Maybe later';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid={`${testIdPrefix}-modal`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testIdPrefix}-title`}
      onClick={(e) => {
        if (e.target === e.currentTarget) void handleAction('dismissed');
      }}
    >
      <div
        className="relative w-full sm:w-[440px] rounded-t-3xl sm:rounded-3xl bg-[#141419] border border-gold/25 shadow-2xl overflow-hidden animate-slideup"
        data-testid={`${testIdPrefix}-sheet`}
      >
        {/* Gold ambient glow — matches mobile's LinearGradient hero */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-gold/20 to-transparent"
          aria-hidden="true"
        />

        {/* Close button */}
        <button
          type="button"
          onClick={() => handleAction('dismissed')}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/5 border border-white/10 grid place-items-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close"
          data-testid={`${testIdPrefix}-close`}
        >
          <X size={16} />
        </button>

        <div className="relative px-6 pt-6 pb-6">
          {/* Brand + star header */}
          <div className="flex items-center gap-3 mb-4">
            {prompt.brand.logo_url ? (
              <img
                src={prompt.brand.logo_url}
                alt=""
                className="w-11 h-11 rounded-xl object-cover bg-black/40"
              />
            ) : null}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white truncate">
                {prompt.brand.name}
              </div>
              {prompt.branch_name ? (
                <div className="text-xs text-gold font-medium mt-0.5 truncate">
                  {prompt.branch_name}
                </div>
              ) : null}
            </div>
            <div className="w-10 h-10 rounded-full bg-gold/15 border border-gold/30 grid place-items-center shrink-0">
              <Star size={18} className="text-gold fill-gold" />
            </div>
          </div>

          {/* Copy */}
          <h2
            id={`${testIdPrefix}-title`}
            className="font-display text-2xl font-bold text-white leading-tight"
            data-testid={`${testIdPrefix}-title`}
          >
            {title}
          </h2>
          <p
            className="text-sm text-white/70 mt-2 leading-relaxed"
            data-testid={`${testIdPrefix}-body`}
          >
            {body}
          </p>

          {/* CTAs */}
          <button
            type="button"
            onClick={() => handleAction('clicked')}
            data-testid={`${testIdPrefix}-now`}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-2xl px-5 py-3.5 text-sm hover:bg-gold-light transition-colors shadow-glow-gold-sm min-h-[48px]"
          >
            <ExternalLink size={16} />
            {primaryLabel}
          </button>
          <button
            type="button"
            onClick={() => handleAction('dismissed')}
            data-testid={`${testIdPrefix}-later`}
            className="mt-2.5 w-full inline-flex items-center justify-center gap-2 bg-transparent border border-gold/35 text-gold font-semibold rounded-2xl px-5 py-3 text-sm hover:bg-gold/10 transition-colors min-h-[44px]"
          >
            {secondaryLabel}
          </button>
          {neverAgainLabel ? (
            <button
              type="button"
              onClick={() => handleAction('never_again_this_brand')}
              data-testid={`${testIdPrefix}-never`}
              className="mt-3 w-full text-xs text-white/50 hover:text-white/80 underline underline-offset-2 py-2"
            >
              {neverAgainLabel}
            </button>
          ) : null}
        </div>
      </div>

      <style>{`
        @keyframes r7-slideup {
          from { transform: translateY(60px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-slideup { animation: r7-slideup 320ms cubic-bezier(0.22,1,0.36,1); }
      `}</style>
    </div>
  );
}
